import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { PhFilePdf, PhWarning } from 'ember-phosphor-icons';
import type { TOC } from '@ember/component/template-only';

interface NotValidatedBannerSignature {
  Args: {
    filename: string;
    onUseAnyways: () => void;
    onDiscard: () => void;
  };
  Element: HTMLDivElement;
}

/**
 * Amber "was flagged" alert shown when AI processing finished but could not
 * classify the document into any of the configured types (e.g.
 * detected_document_types is empty or only "general"). Mirrors the yellow
 * "Mini-Alert" rows in Figma (4449-2773, 4449-2871).
 *
 * The employee can `use anyways` (accept the unverified file) or `discard`.
 */
const NotValidatedBanner: TOC<NotValidatedBannerSignature> = <template>
  <div
    class="flex items-start gap-3 rounded-b-xl border-l-4 border-amber-500 bg-amber-50 px-4 py-3"
    role="status"
    data-test-not-validated-banner
    ...attributes
  >
    <PhWarning @size="18" @weight="fill" class="mt-0.5 shrink-0 text-amber-600" />
    <div class="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-sm">
      <PhFilePdf @size="14" class="shrink-0 text-amber-700" />
      <span class="truncate font-medium text-amber-800">{{@filename}}</span>
      <span class="text-amber-700">
        {{t "tas.attachments.not_validated_message"}}
      </span>
    </div>
    <div class="flex shrink-0 items-center gap-3 text-sm">
      <button
        type="button"
        class="font-semibold text-amber-800 underline hover:text-amber-900"
        data-test-not-validated-use-anyways
        {{on "click" @onUseAnyways}}
      >
        {{t "tas.attachments.use_anyways"}}
      </button>
      <span class="text-amber-300">|</span>
      <button
        type="button"
        class="font-semibold text-amber-800 underline hover:text-amber-900"
        data-test-not-validated-discard
        {{on "click" @onDiscard}}
      >
        {{t "tas.attachments.discard"}}
      </button>
    </div>
  </div>
</template>;

export default NotValidatedBanner;
